import React, { useState, useRef, useEffect } from 'react';
import { 
  Baby, UserPlus, Camera, ArrowLeft, Edit3, 
  ShieldCheck, Save, Trash2, Share2, UserCheck, 
  Mail, X, MessageCircle, Bell, 
  Check, Phone, Clock, Shield, ChevronRight, Info 
} from 'lucide-react';

// ✅ IMPORTACIONES CON RUTAS ABSOLUTAS
import { useGlobal } from 'context/GlobalContext';
import { useAuth } from 'context/AuthContext';

const MisNinos = () => {
  const { ninos, setNinos } = useGlobal(); // Acceso a Firebase vía GlobalContext
  const { user } = useAuth(); // Identidad del tutor logueado

  // --- ESTADOS DE VISTA ---
  const [vista, setVista] = useState('lista');
  const [paso, setPaso] = useState(1);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [ninoEditandoId, setNinoEditandoId] = useState(null);
  const [mostrandoCompartir, setMostrandoCompartir] = useState(null);
  const [tipoCompartir, setTipoCompartir] = useState('permanente');
  const fileInputRef = useRef(null);

  // --- RELOJ PARA CUSTODIAS ---
  const [ahora, setAhora] = useState(Date.now());
  useEffect(() => {
    const intervalo = setInterval(() => setAhora(Date.now()), 60000); 
    return () => clearInterval(intervalo);
  }, []);

  const [solicitudes, setSolicitudes] = useState([
    { id: 101, remitente: 'Elena Ríos', remitenteTel: '9933112233', ninoNombre: 'SOFÍA PÉREZ', ninoEdad: '2 AÑOS', temporal: false }
  ]);

  const [formData, setFormData] = useState({
    nombre: '', fechaNacimiento: '', tipo: 'hijo', emergencia: ''
  });

  // --- FUNCIONES CORE ---
  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const calcularEdad = (fecha) => {
    if(!fecha) return "";
    const hoy = new Date();
    const cumple = new Date(fecha);
    let edad = hoy.getFullYear() - cumple.getFullYear();
    if (hoy.getMonth() < cumple.getMonth() || (hoy.getMonth() === cumple.getMonth() && hoy.getDate() < cumple.getDate())) edad--;
    return edad < 2 ? `${edad} AÑO` : `${edad} AÑOS`;
  };

  const calcularTiempoRestante = (expiracion) => {
    if (!expiracion) return "PERMANENTE";
    const faltan = expiracion - ahora;
    if (faltan <= 0) return "VENCIDO";
    const horas = Math.floor(faltan / (1000 * 60 * 60));
    const minutos = Math.floor((faltan % (1000 * 60 * 60)) / (1000 * 60));
    return `Quedan ${horas}h ${minutos}m`;
  };

  const iniciarFormulario = (tipo) => {
    setNinoEditandoId(null);
    setFormData({ nombre: '', fechaNacimiento: '', tipo: tipo, emergencia: user?.phone || '' });
    setFotoPreview(null);
    setPaso(1);
    setVista('formulario');
  };

  const editarNino = (nino) => {
    if(nino.tipo === 'encargo') {
      alert("⚠️ Seguridad: Solo el tutor legal puede modificar este expediente.");
      return;
    }
    setNinoEditandoId(nino.id);
    setFormData({ ...nino });
    setFotoPreview(nino.foto);
    setPaso(1);
    setVista('formulario');
  };

  const guardarRegistro = () => {
    if (!formData.nombre || !formData.fechaNacimiento || !formData.emergencia) {
      alert("⚠️ Datos incompletos para el seguro.");
      return;
    }

    const ninoFinal = {
      ...formData,
      id: ninoEditandoId || Date.now(),
      foto: fotoPreview,
      nombre: formData.nombre.toUpperCase(),
      edad: calcularEdad(formData.fechaNacimiento),
      estatus: 'fuera',
      tutores: [{ nombre: user?.displayName, tel: user?.phone }],
      salon: ninoEditandoId ? ninos.find(n => n.id === ninoEditandoId)?.salon : "SIN ASIGNAR"
    };

    if (ninoEditandoId) {
      setNinos(ninos.map(n => n.id === ninoEditandoId ? ninoFinal : n));
    } else {
      setNinos([...ninos, ninoFinal]);
    }
    setVista('lista');
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto space-y-12 animate-in fade-in duration-500 pb-20 px-4 text-left">
      
      {vista === 'lista' ? (
        <div className="space-y-12">
          
          {/* SOLICITUDES DE CUSTODIA */}
          {solicitudes.length > 0 && (
            <div className="bg-amber-100 border-4 border-amber-500 p-6 rounded-[2.5rem] shadow-xl">
              <h3 className="text-xl font-black text-amber-900 uppercase flex items-center gap-3 mb-6">
                <Bell className="animate-bounce" size={24} /> ¡Petición de Custodia!
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {solicitudes.map(s => (
                  <div key={s.id} className="bg-white p-6 rounded-3xl border-2 border-amber-300 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4 text-left">
                      <div className="w-14 h-14 bg-amber-500 text-white rounded-2xl flex items-center justify-center shrink-0">
                        <UserCheck size={28}/>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase">Te confían a:</p>
                        <h4 className="text-xl font-black text-slate-900 leading-none uppercase italic">{s.ninoNombre}</h4>
                        <p className="text-[10px] font-bold text-amber-600 mt-1 uppercase italic">{s.temporal ? 'Solo por hoy' : 'Acceso Permanente'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button onClick={() => {}} className="flex-1 sm:flex-none bg-emerald-500 text-white px-6 py-3 rounded-xl font-black text-xs uppercase shadow-md">Aceptar</button>
                      <button onClick={() => setSolicitudes(solicitudes.filter(x => x.id !== s.id))} className="p-3 bg-slate-100 text-slate-400 rounded-xl"><X size={20}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LISTA DE EXPLORADORES */}
          <div className="space-y-8 text-left">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b-4 border-slate-200 pb-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-black text-slate-900 italic tracking-tighter uppercase">Mi Familia</h2>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 italic">Expedientes registrados en Casa de Fe</p>
              </div>
              <button onClick={() => iniciarFormulario('hijo')} className="bg-slate-900 text-white px-8 py-5 rounded-[2rem] flex items-center justify-center gap-3 shadow-2xl hover:bg-indigo-600 transition-all active:scale-95 group">
                <UserPlus size={24} className="text-emerald-400 group-hover:rotate-12 transition-transform" /> 
                <span className="font-black text-sm uppercase tracking-widest">Registrar Nuevo</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ninos.map(nino => {
                const tiempoRestante = calcularTiempoRestante(nino.expiracion);
                const vencido = tiempoRestante === "VENCIDO";

                return (
                  <div key={nino.id} className={`bg-white p-8 rounded-[3.5rem] border-4 shadow-xl flex flex-col items-center text-center space-y-6 relative overflow-hidden transition-all ${vencido ? 'border-rose-500 grayscale opacity-60' : 'border-slate-50 hover:border-indigo-100'}`}>
                    
                    <div className={`absolute top-0 w-full py-2 font-black text-[9px] uppercase tracking-[0.3em] ${nino.tipo === 'hijo' ? 'bg-emerald-500 text-white' : vencido ? 'bg-rose-500 text-white' : 'bg-indigo-500 text-white'}`}>
                      {nino.tipo === 'hijo' ? 'Vínculo Directo' : 'Custodia Compartida'}
                    </div>

                    <div className="pt-8 w-32 h-32 rounded-[2.5rem] overflow-hidden border-4 border-slate-100 bg-slate-50 flex items-center justify-center text-slate-300 mt-4 shadow-inner">
                      {nino.foto ? <img src={nino.foto} className="w-full h-full object-cover" /> : <Baby size={60} strokeWidth={1} />}
                    </div>

                    <div className="space-y-2 w-full">
                      <h3 className="text-2xl font-black text-slate-900 leading-tight uppercase italic tracking-tight">{nino.nombre}</h3>
                      <p className="text-[10px] font-black text-slate-400 bg-slate-50 py-1.5 rounded-xl uppercase tracking-widest border border-slate-100 italic">
                        {nino.salon} • {nino.edad}
                      </p>
                      
                      {nino.expiracion && (
                        <div className={`flex items-center justify-center gap-2 py-2 rounded-xl font-black text-[9px] uppercase ${vencido ? 'text-rose-600 bg-rose-50' : 'text-amber-700 bg-amber-50'}`}>
                          <Clock size={14}/> {vencido ? 'VENCIDO' : tiempoRestante}
                        </div>
                      )}
                    </div>

                    <div className="w-full grid grid-cols-2 gap-3 mt-auto">
                      {nino.tipo === 'hijo' ? (
                        <>
                          <button onClick={() => editarNino(nino)} className="py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200">Editar</button>
                          <button onClick={() => setMostrandoCompartir(nino)} className="py-4 bg-indigo-50 text-indigo-600 border-2 border-indigo-100 rounded-2xl font-black text-[10px] uppercase tracking-widest">Compartir</button>
                        </>
                      ) : (
                        <button onClick={() => setNinos(ninos.filter(x => x.id !== nino.id))} className="col-span-2 py-4 bg-rose-50 text-rose-500 rounded-2xl font-black text-[10px] uppercase">Finalizar Supervisión</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* FORMULARIO */
        <div className="max-w-3xl mx-auto animate-in slide-in-from-bottom-8 pb-10">
          <button onClick={() => setVista('lista')} className="flex items-center gap-2 text-slate-400 font-black text-xs uppercase hover:text-slate-900 mb-8 ml-4">
            <ArrowLeft size={16} /> Volver a mi familia
          </button>
          
          <div className="bg-white rounded-[4rem] shadow-2xl border-4 border-slate-100 overflow-hidden">
            <div className="bg-[#0A2440] p-10 text-white text-center">
              <span className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-400 italic">Protocolo de Seguridad</span>
              <h3 className="text-4xl font-black uppercase italic tracking-tighter mt-2">
                {paso === 1 ? 'Identidad' : 'Emergencia'}
              </h3>
            </div>

            <div className="p-10 md:p-16 space-y-10">
              {paso === 1 ? (
                <>
                  <div className="flex flex-col items-center">
                    <div onClick={() => fileInputRef.current.click()} className="w-44 h-44 bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:bg-indigo-50 transition-all overflow-hidden group">
                      {fotoPreview ? <img src={fotoPreview} className="w-full h-full object-cover" /> : <><Camera size={48} className="text-slate-300 group-hover:text-indigo-400" /> <span className="text-[9px] font-black text-slate-400 mt-2">FOTO RECIENTE</span></>}
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handleFotoChange} className="hidden" accept="image/*" />
                  </div>

                  <div className="space-y-6 text-left">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-6">Nombre Completo del Niño</label>
                      <input value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value.toUpperCase()})} className="w-full p-6 bg-slate-50 rounded-[2rem] font-black text-xl outline-none focus:ring-4 ring-indigo-500/10 uppercase" placeholder="EJ: MATEO PÉREZ" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-6">Fecha de Nacimiento</label>
                      <input type="date" value={formData.fechaNacimiento} onChange={(e) => setFormData({...formData, fechaNacimiento: e.target.value})} className="w-full p-6 bg-slate-50 rounded-[2rem] font-black text-xl outline-none focus:ring-4 ring-indigo-500/10" />
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-8 text-left">
                  <div className="bg-emerald-50 p-6 rounded-[2.5rem] border-2 border-emerald-100 flex items-center gap-6">
                    <ShieldCheck size={40} className="text-emerald-500 shrink-0"/>
                    <p className="text-sm font-bold text-emerald-800 leading-tight italic">Este contacto es exclusivo para avisos sanitarios o requerimientos inmediatos del salón.</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-6">WhatsApp de Emergencia</label>
                    <div className="relative">
                      <Phone size={24} className="absolute left-6 top-1/2 -translate-y-1/2 text-indigo-300"/>
                      <input type="tel" value={formData.emergencia} onChange={(e) => setFormData({...formData, emergencia: e.target.value})} className="w-full pl-16 p-6 bg-slate-50 rounded-[2rem] font-black text-3xl outline-none focus:ring-4 ring-indigo-500/10" placeholder="9930000000" />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-6">
                {paso === 2 && <button onClick={() => setPaso(1)} className="flex-1 py-6 bg-slate-100 text-slate-500 rounded-[2rem] font-black uppercase text-xs">Volver</button>}
                <button onClick={() => paso === 1 ? setPaso(2) : guardarRegistro()} className="flex-[2] py-6 bg-slate-900 text-white rounded-[2rem] font-black uppercase text-xs shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all">
                  {paso === 2 ? <><Save size={20}/> Finalizar Registro</> : <>Continuar <ChevronRight size={20}/></>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COMPARTIR */}
      {mostrandoCompartir && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[100] flex items-center justify-center p-4 text-left">
          <div className="bg-white w-full max-w-xl rounded-[4rem] shadow-2xl overflow-hidden animate-in zoom-in-95 border-[6px] border-white">
            <div className="bg-indigo-600 p-8 text-white text-center relative">
              <button onClick={() => setMostrandoCompartir(null)} className="absolute top-6 right-6 text-indigo-200"><X size={28}/></button>
              <h3 className="text-3xl font-black uppercase italic tracking-tighter leading-none">Compartir Acceso</h3>
              <p className="text-[10px] font-black opacity-60 uppercase mt-2 tracking-[0.3em]">{mostrandoCompartir.nombre}</p>
            </div>

            <div className="p-10 space-y-8">
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setTipoCompartir('permanente')} className={`p-6 rounded-[2.5rem] border-4 flex flex-col items-center gap-2 transition-all ${tipoCompartir === 'permanente' ? 'border-indigo-600 bg-indigo-50 text-indigo-600 shadow-lg' : 'border-slate-100 text-slate-300'}`}>
                  <Shield size={32}/> <span className="font-black text-[10px]">RECOGIDA TOTAL</span>
                </button>
                <button onClick={() => setTipoCompartir('temporal')} className={`p-6 rounded-[2.5rem] border-4 flex flex-col items-center gap-2 transition-all ${tipoCompartir === 'temporal' ? 'border-amber-500 bg-amber-50 text-amber-600 shadow-lg' : 'border-slate-100 text-slate-300'}`}>
                  <Clock size={32}/> <span className="font-black text-[10px]">SOLO POR HOY</span>
                </button>
              </div>

              <div className="space-y-3">
                <button onClick={() => {}} className="w-full p-6 bg-emerald-500 text-white rounded-[2rem] font-black uppercase text-xs flex items-center justify-between px-10 shadow-xl active:scale-95 transition-all">
                  <div className="flex items-center gap-4"><MessageCircle size={28}/> Enviar por WhatsApp</div> <ChevronRight size={24}/>
                </button>
                <button onClick={() => {}} className="w-full p-6 bg-slate-100 text-slate-400 rounded-[2rem] font-black uppercase text-xs flex items-center justify-between px-10">
                  <div className="flex items-center gap-4"><Share2 size={28}/> Copiar Enlace Seguro</div> <ChevronRight size={24}/>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MisNinos;